import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import DashboardPage from "@/pages/DashboardPage";
import ClientsPage from "@/pages/ClientsPage";
import ClientDetailPage from "@/pages/ClientDetailPage";
import NewClientPage from "@/pages/NewClientPage";
import EditClientPage from "@/pages/EditClientPage";
import EmployeesPage from "@/pages/EmployeesPage";
import EmployeeDetailPage from "@/pages/EmployeeDetailPage";
import SchedulePage from "@/pages/SchedulePage";
import ReferralsPage from "@/pages/ReferralsPage";
import PhysicianSignPage from "@/pages/PhysicianSignPage";
import PCWSignPage from "@/pages/PCWSignPage";
import PhysiciansPage from "@/pages/PhysiciansPage";
import OrderTrackingPage from "@/pages/OrderTrackingPage";
import CommunicationPage from "@/pages/CommunicationPage";
import HRPayrollPage from "@/pages/HRPayrollPage";
import SettingsPage from "@/pages/SettingsPage";
import EVVPage from "@/pages/EVVPage";
import EvaluationReportsPage from "@/pages/EvaluationReportsPage";
import PublicJobApplicationPage from "@/pages/PublicJobApplicationPage";
import PublicClientReferralPage from "@/pages/PublicClientReferralPage";
import CaregiverPortalPage from "@/pages/CaregiverPortalPage";
import TrainingPage from "@/pages/TrainingPage";
import FaxesPage from "@/pages/FaxesPage";
import ROISignPage from "@/pages/ROISignPage";
import OrientationPage from "@/pages/OrientationPage";
import BackgroundCheckPage from "@/pages/BackgroundCheckPage";
import PlatformPage from "./pages/PlatformPage";
import PrintQueuePage from "@/pages/PrintQueuePage";
import RnSignaturesPage from "@/pages/RnSignaturesPage";
import RnAssessmentsPage from "@/pages/RnAssessmentsPage";

// Context
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PermissionsProvider } from "@/context/PermissionsContext";

// Protected Route Component
const ProtectedRoute = ({ children, allowWithoutSubscription = false }) => {
  const { user, loading, subscriptionValid } = useAuth();
  const location = window.location.pathname;
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Check if user is a caregiver/PCW with limited access
  const caregiverRoles = ['pcw', 'caregiver', 'care_worker', 'personal_care_worker'];
  const isCaregiverRole = caregiverRoles.includes(user.role?.toLowerCase());
  const isCaregiverPage = location.startsWith("/caregiver");
  
  // If caregiver tries to access non-caregiver pages, redirect to caregiver portal
  if (isCaregiverRole && !isCaregiverPage) {
    return <Navigate to="/caregiver" replace />;
  }
  
  // Caregivers can access their portal regardless of subscription status (agency pays, not them)
  if (isCaregiverRole && isCaregiverPage) {
    return children;
  }
  
  // If subscription is not valid, redirect to subscription page
  // Unless explicitly allowed or on the subscribe page itself
  if (!subscriptionValid && !allowWithoutSubscription) {
    return <Navigate to="/subscribe" replace />;
  }
  
  return children;
};

// Caregiver-only route (for the caregiver portal)
const CaregiverRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Allow caregivers and admins to access the caregiver portal
  return children;
};

// Public Route (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }
  
  // Redirect caregivers to their portal, others to dashboard
  if (user) {
    const caregiverRoles = ['pcw', 'caregiver', 'care_worker', 'personal_care_worker'];
    if (caregiverRoles.includes(user.role?.toLowerCase())) {
      return <Navigate to="/caregiver" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Subscription Route (requires login, redirects to dashboard if already subscribed)
const SubscriptionRoute = ({ children }) => {
  const { user, loading, subscriptionValid } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Allow access if returning from Stripe checkout with session_id
  const params = new URLSearchParams(window.location.search);
  if (params.get("session_id")) {
    return children;
  }
  
  // If already subscribed, go to dashboard
  if (subscriptionValid) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Subscription/Onboarding - Requires login but not subscription */}
      <Route
        path="/subscribe"
        element={
          <SubscriptionRoute>
            <SubscriptionPage />
          </SubscriptionRoute>
        }
      />
      
      {/* Public Application Forms - Unique per Agency */}
      <Route path="/apply/:agencyCode" element={<PublicJobApplicationPage />} />
      <Route path="/refer/:agencyCode" element={<PublicClientReferralPage />} />
      
      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients"
        element={
          <ProtectedRoute>
            <ClientsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients/new"
        element={
          <ProtectedRoute>
            <NewClientPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients/:id"
        element={
          <ProtectedRoute>
            <ClientDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients/:id/edit"
        element={
          <ProtectedRoute>
            <EditClientPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/employees"
        element={
          <ProtectedRoute>
            <EmployeesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/employees/:id"
        element={
          <ProtectedRoute>
            <EmployeeDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/schedule"
        element={
          <ProtectedRoute>
            <SchedulePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/referrals"
        element={
          <ProtectedRoute>
            <ReferralsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/physicians"
        element={
          <ProtectedRoute>
            <PhysiciansPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/order-tracking"
        element={
          <ProtectedRoute>
            <OrderTrackingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/communication"
        element={
          <ProtectedRoute>
            <CommunicationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/faxes"
        element={
          <ProtectedRoute>
            <FaxesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr-payroll"
        element={
          <ProtectedRoute>
            <HRPayrollPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/evv"
        element={
          <ProtectedRoute>
            <EVVPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/evaluation-reports"
        element={
          <ProtectedRoute>
            <EvaluationReportsPage />
          </ProtectedRoute>
        }
      />
      
      {/* Caregiver Portal (mobile-friendly clock in/out) */}
      <Route
        path="/caregiver"
        element={
          <ProtectedRoute>
            <CaregiverPortalPage />
          </ProtectedRoute>
        }
      />
      
      {/* Training Center */}
      <Route
        path="/training"
        element={
          <ProtectedRoute>
            <TrainingPage />
          </ProtectedRoute>
        }
      />

      {/* Office Print Queue (admin) */}
      <Route
        path="/print-queue"
        element={
          <ProtectedRoute>
            <PrintQueuePage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/platform"
        element={
          <ProtectedRoute>
            <PlatformPage />
          </ProtectedRoute>
        }
      />

      {/* RN co-signature queue */}
      <Route
        path="/rn-signatures"
        element={
          <ProtectedRoute>
            <RnSignaturesPage />
          </ProtectedRoute>
        }
      />

      {/* RN client assessments (Admission / Supervisory / Re-Admission) */}
      <Route
        path="/rn-assessments"
        element={
          <ProtectedRoute>
            <RnAssessmentsPage />
          </ProtectedRoute>
        }
      />
      
      {/* Public Physician Sign Route (no auth required) */}
      <Route path="/physician-sign/:token" element={<PhysicianSignPage />} />
      
      {/* Public PCW Evaluation Sign Route (no auth required) */}
      <Route path="/pcw-sign/:token" element={<PCWSignPage />} />

      {/* Public ROI Sign Route (no auth required) */}
      <Route path="/roi-sign/:token" element={<ROISignPage />} />
      <Route path="/orientation/:token" element={<OrientationPage />} />
      <Route path="/background-check/:token" element={<BackgroundCheckPage />} />
      
      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PermissionsProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors closeButton />
        </PermissionsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
